import { BaseResponse } from '../data/types';
import { Zone } from '../projects/types';

interface SwData {
  data: number[];
}

interface PcData {
  data: number[];
}

export interface ConversionParameters {
  laboratory_ift: number | null;
  laboratory_angle: number | null;
  ift_cos_lab: number | null;
  reservoir_ift: number | null;
  reservoir_angle: number | null;
  ift_cos_res: number | null;
  oil_density: number | null;
}

export interface InputSampleValues {
  fzi: number | null;
  rqi: number | null;
  phiz: number | null;
  ko: number | null;
  krg: number | null;
  krg_absolute: number | null;
  kro_absolute_g: number | null;
  kro_absolute: number | null;
  krw: number | null;
  krw_absolute: number | null;
  permeability: number | null;
  phi_res: number | null;
  porosity: number | null;
  qv: number | null;
  salinity: number | null;
  sorg: number | null;
  swi: number | null;
}

export interface LiaoSampleCurves {
  sw: number[] | null;
  pc: number[] | null;
}

export interface WaterOilSampleCurves {
  sw_krel: number[] | null;
  krw_lab: number[] | null;
  kro_lab: number[] | null;
  fw_lab: number[] | null;
}

export interface GasOilSampleCurves {
  sg_krel: number[] | null;
  krg_lab: number[] | null;
  kro_lab: number[] | null;
  fg_lab: number[] | null;
}

export interface InputSampleCurves {
  water_oil: WaterOilSampleCurves | null;
  gas_oil: GasOilSampleCurves | null;
  pc_model: LiaoSampleCurves | null;
}

export interface RelativePermeabilityAnalysisValues {
  water_oil: {
    sor_w: number | null;
    true_sor_w: number | null;
    krw_exponent: number | null;
    true_krw: number | null;
    true_kro_w: number | null;
    no_w: number | null;
    nw: number | null;
    true_no_w: number | null;
    true_nw: number | null;
  };
  gas_oil: {
    sor: number | null;
    true_sor_g: number | null;
    sor_g: number | null;
    sgc: number | null;
    krg_exponent: number | null;
    true_krg: number | null;
    true_kro_g: number | null;
    no_g: number | null;
    ng: number | null;
    true_no_g: number | null;
    true_ng: number | null;
  };
}

interface WaterOilRelPermAnalysisCurves {
  krw_corrected: (number | null)[];
  kro_corrected: (number | null)[];
  son: (number | null)[];
  swn: (number | null)[];
  no_exponent: (number | null)[];
  nw_exponent: (number | null)[];
  indexes_no: number[] | null;
  indexes_nw: number[] | null;
}

interface GasOilRelPermAnalysisCurves {
  krg_corrected: (number | null)[];
  kro_corrected: (number | null)[];
  son: (number | null)[];
  sgn: (number | null)[];
  no_exponent: (number | null)[];
  ng_exponent: (number | null)[];
  so: (number | null)[];
  sl: (number | null)[];
  swi_sg: (number | null)[];
  indexes_no: number[] | null;
  indexes_ng: number[] | null;
}

interface RelativePermeabilityAnalysisCurves {
  water_oil: WaterOilRelPermAnalysisCurves | null;
  gas_oil: GasOilRelPermAnalysisCurves | null;
}

export interface WaterOilRefinementCurveSet {
  sw: number[] | null;
  krw_calc: number[] | null;
  kro_calc: number[] | null;
  fw_calc?: number[] | null;
}

export interface GasOilRefinementCurveSet {
  sg: number[] | null;
  krg_scaled: number[] | null;
  kro_scaled: number[] | null;
  krg_calc: number[] | null;
  kro_calc: number[] | null;
}

interface GasOilRefinementCurves {
  lab_based?: GasOilRefinementCurveSet | null;
  refinement_based?: GasOilRefinementCurveSet | null;
  sg: number[] | null;
  kro_calc: number[] | null;
  krg_calc: number[] | null;
  krg_scaled: number[] | null;
  kro_scaled: number[] | null;
  indexes_no: number[] | null;
  indexes_ng: number[] | null;
}

export interface WaterOilRefinementCurves {
  lab_based?: WaterOilRefinementCurveSet | null;
  refinement_based?: WaterOilRefinementCurveSet | null;
  fw_calc: number[] | null;
  kro_calc: number[] | null;
  krw_calc: number[] | null;
  sw: number[] | null;
  indexes_no: number[] | null;
  indexes_nw: number[] | null;
}

export interface RefinementCurves {
  gas_oil: GasOilRefinementCurves | null;
  water_oil: WaterOilRefinementCurves | null;
}

interface CustomAnalyzeValuesUpdate {
  custom_a?: number | null;
  custom_b?: number | null;
  pce?: number | null;
  large_derivative?: number | null;
}

export interface RegressionIndexes {
  son_kro_selected_indexes?: number[] | null;
  swn_krw_selected_indexes?: number[] | null;
  refined_no_w_selected_indexes?: number[] | null;
  refined_nw_selected_indexes?: number[] | null;
  refined_no_g_selected_indexes?: number[] | null;
  refined_ng_selected_indexes?: number[] | null;
  sgn_krg_selected_indexes?: number[] | null;
  son_kro_g_selected_indexes?: number[] | null;
}

export interface AnalysisOutcome {
  status: 'success' | 'failed';
  reason?: string | null;
  fit_error?: number | null;
  fit_rmse?: number | null;
  /** Brooks-Corey: NRMSE of predicted vs measured reservoir Pc. */
  fit_nrmse?: number | null;
  /** Brooks-Corey: maximum absolute residual over the fitted points. */
  fit_max_residual?: number | null;
  r_squared?: number | null;
  n_fit_points?: number | null;
  /** Usable regression points available before automatic selection. */
  n_total_points?: number | null;
  /** How the result was produced: Brooks-Corey auto-selection
   * ('contiguous' / 'non_contiguous') or the Kr pipeline ('lab-only',
   * 'refined', 'manual', 'preserved', 'borderline'). */
  candidate_type?:
    | 'contiguous'
    | 'non_contiguous'
    | 'lab-only'
    | 'refined'
    | 'manual'
    | 'preserved'
    | 'borderline'
    | null;
  /** Usable base points excluded from the fit (endpoints + internal). */
  excluded_indexes?: number[] | null;
  /** Interior points automatically excluded as suspected outliers. */
  internal_excluded_indexes?: number[] | null;
  /** True when Swi was absent/invalid and swir fell back to min measured Sw. */
  swi_inferred?: boolean | null;
  /** Human-readable explanation of the automatic point-subset choice. */
  selection_note?: string | null;
  /** Backend-generated review reasons (borderline fit, fallback, etc.). */
  warnings?: string[] | null;
  /** Kr refinement: endpoint (Swn-Krw / Sgn-Krg) regression R². */
  secondary_r_squared?: number | null;
  /** Kr refinement: Sw@Kro=Krw (or Sg@Kro=Krg) crossover saturation. */
  crossover_saturation?: number | null;
  /** Kr refinement: No+Nw (or No+Ng) quality-band value (7.0 - 8.5). */
  exponent_sum?: number | null;
}

export interface Sample {
  uid: string;
  depth: number;
  sample_length?: number | null;
  well_uid: string;
  project_uid: string;
  sample_code: string;
  input_sample_values: InputSampleValues;
  input_sample_curves: InputSampleCurves;
  k_rel_values: RelativePermeabilityAnalysisValues | null;
  k_rel_curves: RelativePermeabilityAnalysisCurves | null;
  refinement_curves: RefinementCurves | null;
  lab_system: System;
  reservoir_system: System;
  analysis_values: {
    lambda_values: LambdaSampleResults | null;
    liao: LiaoSampleResults | null;
  };
  analysis_curves: {
    lambda_curves: AnalysisResultsLambda | null;
    liao: AnalysisResultsLiao | null;
  };
  ift_cos_res: number | null;
  ift_cos_lab: number | null;
  conversion_parameters: ConversionParameters;
  water_grad: number | null;
  oil_grad: number | null;
  performed_analyses?: CalculationStrategies[];
  analysis_outcomes?: Record<string, AnalysisOutcome> | null;
  version: number;
  depth_unit?: "m" | "ft";
  pressure_unit?: "psi" | "bar";
  regression_indexes?: RegressionIndexes | null;
}

export interface Curve {
  curve_name: string;
  description?: string | null;
  data: number[];
  uid: string;
  well_uid: string;
}

export interface LiaoSample {
  sample_name: '';
  depth: number;
  k_air?: number | null;
  phi?: number | null;
  sw: SwData;
  pc: PcData;
  well_uid: string;
  results: null;
}

export interface SampleMeasuredProps {
  inputFields: { value: string }[];
  inputCapillary: { value: string }[];
  handleInputChange: (
    index: number,
    event: React.ChangeEvent<HTMLInputElement>,
    type: string
  ) => void;
  handleAddInput: () => void;
  handleRemoveInput: (index: number, type: string) => void;
  setInputFields: React.Dispatch<
    React.SetStateAction<
      {
        value: string;
      }[]
    >
  >;
  setInputCapillary: React.Dispatch<
    React.SetStateAction<
      {
        value: string;
      }[]
    >
  >;
}

export interface ProjectSample extends Sample {
  selectedAnalyses: Record<CalculationStrategies, boolean>;
  performed_analyses: CalculationStrategies[];
  selected: boolean;
}

export interface LiaoSampleResults {
  swir: number | null;
  pc_max: number | null;
  best_a: number | null;
  best_b: number | null;
  lambda_variable: number | null;
  true_swd: number | null;
  true_swir: number | null;
  true_pcd: number | null;
  true_pc_max: number | null;
  fzi: number | null;
  f_value: number | null;
  pce: number | null;
  large_derivative: number | null;
}

export type LambdaMethod =
  | "mean_slope"
  | "ols_loglog"
  | "nlsq"
  | "weighted_nlsq";

export interface LambdaSampleResults {
  swir?: number | null;
  ln_swn_sum: number | null;
  ln_pc_sum: number | null;
  ln_swn_squared_sum: number | null;
  ln_pc_squared_sum: number | null;
  lnswn_x_lnpc_sum: number | null;
  pe: number | null;
  lambda_value: number | null;
  pc_max?: number | null;
  method?: LambdaMethod | null;
  fit_rmse?: number | null;
}

export type NonNullableLiaoSampleResults = {
  [K in keyof LiaoSampleResults]: NonNullable<LiaoSampleResults[K]>;
};

export type NonNullableLambdaSampleResults = {
  [K in keyof LambdaSampleResults]: NonNullable<LambdaSampleResults[K]>;
};

export interface AnalysisResultsLiao {
  CorrectedSw: (number | null)[] | null;
  CorrectedPc: (number | null)[] | null;
  PceMatch: (number | null)[] | null;
  Pres: (number | null)[] | null;
  PcD: (number | null)[] | null;
  SwD: (number | null)[] | null;
  x: (number | null)[] | null;
  y: (number | null)[] | null;
  Pc_derivative: (number | null)[] | null;
  height: (number | null)[] | null;
}

export interface AnalysisResultsLambda {
  CorrectedSw: (number | null)[] | null;
  CorrectedPc: (number | null)[] | null;
  Pres: (number | null)[] | null;
  PcD: (number | null)[] | null;
  SwD: (number | null)[] | null;
  ln_swn: (number | null)[] | null;
  ln_pc: (number | null)[] | null;
  ln_swn_squared: (number | null)[] | null;
  ln_pc_squared: (number | null)[] | null;
  lnswn_x_lnpc?: (number | null)[] | null;
  ln_swn_x_ln_pc: (number | null)[] | null;
  lambda_curve: (number | null)[] | null;
  indexes: number[] | null
}

export type NonNullableLiaoAnalysisResults = {
  [K in keyof AnalysisResultsLiao]: NonNullable<AnalysisResultsLiao[K]>;
};

export type NonNullableLambdaAnalysisResults = {
  [K in keyof AnalysisResultsLambda]: NonNullable<AnalysisResultsLambda[K]>;
};

export type NonNullableWaterOilInputSampleCurves = {
  [K in keyof (WaterOilSampleCurves & LiaoSampleCurves)]: NonNullable<
    (WaterOilSampleCurves & LiaoSampleCurves)[K]
  >;
};

export interface ZonesInputProps {
  fziMin: { value: string }[];
  fziMax: { value: string }[];
  fziMean: { value: string }[];
  color: { value: string }[];
  setColor: React.Dispatch<
    React.SetStateAction<
      {
        value: string;
      }[]
    >
  >;
  handleFziChange: (
    index: number,
    event: React.ChangeEvent<HTMLInputElement>,
    type: string
  ) => void;
  handleAddZone: () => void;
  handleRemoveZone: () => void;
  setFziMin: React.Dispatch<
    React.SetStateAction<
      {
        value: string;
      }[]
    >
  >;
  setFziMax: React.Dispatch<
    React.SetStateAction<
      {
        value: string;
      }[]
    >
  >;
  setFziMean: React.Dispatch<
    React.SetStateAction<
      {
        value: string;
      }[]
    >
  >;
  lines: number;
  setlines: React.Dispatch<React.SetStateAction<number>>;
  minimumFzi: string | null;
  maximumFzi: string | null;
  zones: Zone[];
}

export enum System {
  AIRBRINE = 'Air-Brine',
  AIROIL = 'Air-Oil',
  OILBRINE = 'Oil-Brine',
  AIRHG = 'Air-Hg'
}

export enum RockType {
  SANDSTONE = 'Sandstone',
  LIMESTONE = 'Limestone'
}

export enum AnalyzeType {
  LIAO = 'LIAO',
  LAMBDA = 'LAMBDA'
}

type AddSamplePayloadSampleCurves = {
  [K in keyof InputSampleCurves]: Partial<InputSampleCurves[K]>;
};

export interface AddSamplePayload {
  well_uid: string;
  project_uid: string;
  depth: number | null;
  sample_length?: number | null;
  sample_code: string;
  input_sample_values: Partial<InputSampleValues>;
  input_sample_curves: Partial<AddSamplePayloadSampleCurves>;
  lab_system?: System;
  reservoir_system?: System;
  water_grad?: number | null;
  oil_grad?: number | null;
  ko?: number | null;
  krw?: number | null;
  depth_unit?: "m" | "ft";
  pressure_unit?: "psi" | "bar";
}

export interface AddSampleResponse extends BaseResponse {
  content: Sample;
}

export interface LiaoInputDTO {
  type: "LIAO";
  bounds?: [number, number][];
  custom_a?: number | null;
  custom_b?: number | null;
  pce?: number | null;
  /** Jointly optimize Pce with any free a/b parameters. */
  optimize_pce?: boolean;
  large_derivative?: number | null;
}

export interface LambdaInputDTO {
  type: "LAMBDA";
  indexes?: number[] | null;
  /** Backend picks the best contiguous point subset (batch mode). */
  auto_select?: boolean;
  method?: LambdaMethod | null;
}

export interface WaterOilInputDTO {
  type: "WATER_OIL";
  input_no?: number | null;
  input_nw?: number | null;
  indexes_no?: number[] | null;
  indexes_nw?: number[] | null;
  /** Run the automated refinement pipeline (true Sor, Krw' endpoint,
   * refined No/Nw), writing the true_* fields + regression indexes. */
  auto_refine?: boolean;
  /** When false (default) auto_refine never replaces existing manual
   * true_* values; send true only after the overwrite confirm. */
  overwrite_refinement?: boolean;
}

export interface GasOilInputDTO {
  type: "GAS_OIL";
  input_no?: number | null;
  input_ng?: number | null;
  indexes_no?: number[] | null;
  indexes_ng?: number[] | null;
  /** See WaterOilInputDTO: automated refinement + overwrite guard. */
  auto_refine?: boolean;
  overwrite_refinement?: boolean;
}

export type InputDataDTO =
  | LiaoInputDTO
  | LambdaInputDTO
  | WaterOilInputDTO
  | GasOilInputDTO;

export interface CalculateSamplePayload {
  sample_uid: string;
  strategies_keys: string[];
  input_data_map: Partial<Record<'liao' | 'lambda' | 'water-oil' | 'gas-oil', InputDataDTO>>;
  /** When true the backend computes but does NOT persist the result (batch preview). */
  preview?: boolean;
}

export interface CalculateProjectPayload {
  sample_uid: string;
  strategies_keys: string[];
}

export interface CalculateSampleResponse extends BaseResponse {
  content: Sample;
}

export interface GetSampleResponse extends BaseResponse {
  content: Sample;
}

export interface ListSamplesResponse extends BaseResponse {
  content: Sample[];
}

export interface FindLiaoBestValuesPayload {
  sample_uid: string;
  optimization_method?: 'L-BFGS-B' | 'BFGS';
  initial_guess: [number, number];
  bounds_a: [number, number];
  bounds_b: [number, number];
}

export interface FindLiaoBestValuesResponse extends BaseResponse {
  content: Sample;
}

export interface AddLiaoCustomPayload {
  sample_uid: string;
  a_value: number;
  b_value: number;
  pce_value?: number;
  large_derivative?: number;
}

export interface AddLiaoCustomResponse extends BaseResponse {
  content: Sample;
}

export interface DeleteSampleResponse extends BaseResponse { }

export interface ListAllProjectSamplesResponse extends BaseResponse {
  content: Sample[];
}

export interface UpdateSamplePayload {
  sample_uid: string;
  well_uid?: string;
  depth?: number | null;
  sample_length?: number | null;
  sample_code?: string;
  lab_system?: System;
  reservoir_system?: System;
  conversion_parameters?: Partial<ConversionParameters>;
  custom_analyze_values?: CustomAnalyzeValuesUpdate;
  input_sample_curves?: InputSampleCurves;
  input_sample_values?: Partial<InputSampleValues>;
  indexes_no?: number;
  indexes_nw?: number;
  depth_unit?: "m" | "ft";
  pressure_unit?: "psi" | "bar";
  regression_indexes?: RegressionIndexes | null;
  true_sor_w?: number | null;
  true_sor_g?: number | null;
  true_kro_w?: number | null;
  true_kro_g?: number | null;
  true_krw?: number | null;
  true_krg?: number | null;
  true_no_w?: number | null;
  true_nw?: number | null;
  true_no_g?: number | null;
  true_ng?: number | null;
  water_grad?: number | null;
  oil_grad?: number | null;
}

export interface UpdateSampleResponse extends BaseResponse {
  content: Sample;
}

export enum CalculationStrategies {
  LIAO = 'liao',
  LAMBDA = 'lambda',
  WATER_OIL_RELATIVE_PERMEABIILITY = 'water-oil',
  GAS_OIL_RELATIVE_PERMEABIILITY = 'gas-oil',
  // SYSTEM_SATURATION_VALUES = 'system_saturation_values',
  // LIAO_SYSTEM_SATURATION = 'liao_system_saturation',
  // LAMBDA_SYSTEM_SATURATION = 'lambda_system_saturation'
}
